import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useRoleManagement } from '../../hooks/roles/useRoleManagement';
import { useRolePermissions } from '../../hooks/permissions/useRolePermissions';
import { usePermissionSelection } from '../../hooks/permissions/usePermissionSelection';
import { permissionService } from '../../services';
import { Permission } from '../../types/permissions';
import LoadingSpinner from '../LoadingSpinner';
import { RoleSelector, PermissionsGrid } from './permissions';
import { RoleManagementModal } from './roles';
import { Button } from '../ui';
import Switch from '../ui/Input/Switch';
import toast from 'react-hot-toast';

const PermissionsManagement: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const { hasPermission } = usePermissions();
  const roleManagement = useRoleManagement();
  const rolePermissions = useRolePermissions(roleManagement.selectedRole?.id || null);
  const permissionSelection = usePermissionSelection(rolePermissions.permissions);

  // Cargar permisos iniciales
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setLoading(true);
        const response = await permissionService.getPermissions();
        if (response.permissions) {
          setPermissions(response.permissions);
        }
      } catch (error) {
        console.error('Error cargando permisos:', error);
        toast.error('Error al cargar los permisos');
       
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, []);

  // Manejar actualización de permisos
  const handleUpdatePermissions = async () => {
    if (!roleManagement.selectedRole) return;

    try {
      setIsUpdating(true);
      const success = await rolePermissions.updateRolePermissions(permissionSelection.selectedIds);
      if (success) {
        // Los permisos se recargan automáticamente en el hook
      }
    } catch (error) {
      console.error('Error actualizando permisos:', error);
      toast.error('Error al actualizar los permisos');
    } finally {
      setIsUpdating(false);
    }
  };

  // Manejar eliminación de rol con confirmación
  const handleDeleteRole = async (roleId: number) => {
    const role = roleManagement.roles.find(r => r.id === roleId);
    if (!role) return;
    
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p>¿Estás seguro de que deseas eliminar el rol "{role.name}"?</p>
        <div className="flex justify-end gap-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            onClick={() => toast.dismiss(t.id)}
          >
            Cancelar
          </button>
          <button
            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
            onClick={async () => {
              toast.dismiss(t.id);
              await roleManagement.handleDeleteRole(roleId);
            }}
          >
            Eliminar
          </button>
        </div>
      </div>
    ), {
      duration: 5000,
      position: 'top-center',
    });
  };

  if (loading) {
    return (
      <div className="p-4 flex justify-center items-center min-h-[200px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!hasPermission('view_roles')) {
    return <div className="p-4 text-red-500">No tienes permisos para ver esta sección</div>;
  }

  const headerTitle = roleManagement.selectedRole
    ? `Permisos de ${roleManagement.selectedRole.name}`
    : 'Gestión de Permisos de Usuarios'

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">{headerTitle}</h2>

      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <RoleSelector
          roles={roleManagement.roles}
          selectedRole={roleManagement.selectedRole}
          onRoleChange={roleManagement.selectRole}
        />
        <div className="flex flex-wrap items-center gap-4">
          {roleManagement.selectedRole && (
            <Switch
              checked={permissionSelection.areAllSelected}
              onChange={permissionSelection.selectAll}
              label="Seleccionar Todos"
              disabled={!hasPermission('manage_permissions')}
            />
          )}
          <div className="flex items-center gap-2">
            {hasPermission('manage_permissions') && roleManagement.selectedRole && (
              <Button
                onClick={handleUpdatePermissions}
                disabled={isUpdating}
                variant="primary"
                isLoading={isUpdating}
              >
                {isUpdating ? 'Actualizando...' : 'Actualizar Permisos'}
              </Button>
            )}
            {hasPermission('manage_roles') && (
              <Button onClick={roleManagement.openModal} variant="success">
                Gestionar Roles
              </Button>
            )}
          </div>
        </div>
      </div>

      {roleManagement.selectedRole && (
        <PermissionsGrid
          permissions={permissions}
          selectedIds={permissionSelection.selectedIds}
          disabled={!hasPermission('manage_permissions')}
          onPermissionToggle={permissionSelection.selectPermission}
        />
      )}

      <RoleManagementModal
        isOpen={roleManagement.showModal}
        onClose={roleManagement.closeModal}
        roles={roleManagement.roles}
        formData={roleManagement.roleForm.formData}
        errors={roleManagement.roleForm.errors}
        isSubmitting={roleManagement.roleForm.isSubmitting}
        onFieldChange={roleManagement.roleForm.updateField}
        onCreateRole={roleManagement.handleCreateRole}
        onDeleteRole={handleDeleteRole}
      />
    </div>
  );
};

export default PermissionsManagement; 